import type {
  WebInteractionAttribution,
  WebInteractionEventName,
} from "../../domain/operations";
import {
  buildLandingVisitAttribution,
  readSessionLandingAttribution,
} from "./landingVisit";

const INTERACTION_STORAGE_PREFIX = "banryeojusik.web-interaction.v1";

interface WebInteractionOptions {
  readonly fetcher?: typeof fetch;
  readonly now?: Date;
  readonly referrer?: string;
  readonly storage?: Storage;
  readonly url?: string;
}

export async function recordWebInteractionOnce(
  eventName: "farm_visit",
  dimension: "owner",
  options: WebInteractionOptions = {},
): Promise<"recorded" | "skipped" | "failed"> {
  const storage = options.storage ?? window.sessionStorage;
  const now = options.now ?? new Date();
  const key = `${INTERACTION_STORAGE_PREFIX}:${eventName}:${localDate(now)}`;
  try {
    if (storage.getItem(key) === "1") return "skipped";
    storage.setItem(key, "1");
  } catch {
    return "failed";
  }
  const result = await sendWebInteraction(eventName, dimension, options);
  if (result === "rejected") {
    try {
      storage.removeItem(key);
    } catch {
      // A later retry is best-effort when browser storage is unavailable.
    }
  }
  return result === "recorded" ? "recorded" : "failed";
}

export async function recordDownloadIntent(
  version: string,
  options: WebInteractionOptions = {},
): Promise<"recorded" | "failed"> {
  if (!/^\d+\.\d+\.\d+$/u.test(version)) return "failed";
  const result = await sendWebInteraction("download_intent", version, options);
  return result === "recorded" ? "recorded" : "failed";
}

async function sendWebInteraction(
  eventName: WebInteractionEventName,
  dimension: string,
  options: WebInteractionOptions,
): Promise<"recorded" | "rejected" | "unknown"> {
  try {
    const currentAttribution = buildLandingVisitAttribution(
      options.url ?? window.location.href,
      options.referrer ?? document.referrer,
    );
    const storedAttribution = eventName === "farm_visit"
      ? readSessionLandingAttribution(options.storage ?? window.sessionStorage)
      : null;
    const payload: WebInteractionAttribution = {
      ...(storedAttribution ?? currentAttribution),
      dimension,
      entryPath: currentAttribution.entryPath,
      eventName,
    };
    const response = await (options.fetcher ?? fetch)(
      "/api/traffic/interaction",
      {
        body: JSON.stringify(payload),
        cache: "no-store",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        method: "POST",
        referrerPolicy: "no-referrer",
      },
    );
    return response.status === 204 ? "recorded" : "rejected";
  } catch {
    return "unknown";
  }
}

function localDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
